import React, { useMemo } from 'react';
import Section from '../../../layout/Section';
import HorizontalTable, { HorizontalTableRow } from '../../../tables/HorizontalTable';
import { GuVYear } from '../hooks/useCalculationPageData';
import { colors } from '../../../../styles/colors';

interface ProfitAndLossSectionProps {
  data: GuVYear[];
}

/**
 * Section-Komponente für die transponierte GuV-Tabelle
 * Jahre als Spalten, Kategorien als Zeilen
 * Verwendet die wiederverwendbare HorizontalTable-Komponente
 */
const ProfitAndLossSection: React.FC<ProfitAndLossSectionProps> = ({ data }) => {
  // Hilfsfunktion für rot/grün-Farbcodierung bei Ergebnis-Zeilen
  const getResultColor = (value: number): string | undefined => {
    if (value < 0) return colors.kpi.gradientTextStart; // rot300 für negative Werte
    if (value > 0) return colors.status.success.main; // grün für positive Werte
    return undefined;
  };

  // Zeilen-Definitionen mit Keys, Labels, Tooltips und optionalem Highlighting
  const rowDefinitions = useMemo(() => [
    {
      key: 'jahresenergieerloes',
      label: 'Energieerlös',
      tooltip: '(Ertragswert − Abzüge) × Zuschlagswert × Korrekturfaktor'
    },
    {
      key: 'zinserloes',
      label: 'Zinserlös',
      tooltip: 'Liquiditätsbestand Vorjahr × Guthabenzins'
    },
    {
      key: 'gesamterloes',
      label: 'Gesamterlös',
      highlight: true,
      tooltip: 'Energieerlös + Zinserlös'
    },
    {
      key: 'laufenderAufwand',
      label: 'Lfd. Aufwand',
      tooltip: 'Summe aller jährlichen Betriebskosten (OPEX)'
    },
    {
      key: 'afa',
      label: 'Abschreibungen',
      tooltip: 'Investitionskosten ÷ Abschreibungsdauer (degressiv)'
    },
    {
      key: 'zinsen',
      label: 'Zinsen',
      tooltip: 'Fremdkapitalzinsen aus Tilgungsplan'
    },
    {
      key: 'gesamtaufwand',
      label: 'Gesamtaufwand',
      highlight: true,
      tooltip: 'Lfd. Aufwand + Abschreibungen + Zinsen'
    },
    {
      key: 'ergebnisVorSteuern',
      label: 'Ergebnis vor Steuern',
      highlight: true,
      tooltip: 'Gesamterlös − Gesamtaufwand',
      valueColor: getResultColor,
    },
    {
      key: 'gewerbesteuer',
      label: 'Gewerbesteuer',
      tooltip: 'Berechnung nach Gewerbesteuergesetz (Hebesatz × Messzahl)'
    },
    {
      key: 'ergebnisNachSteuern',
      label: 'Ergebnis nach Steuern',
      highlight: true,
      tooltip: 'Ergebnis vor Steuern − Gewerbesteuer',
      valueColor: getResultColor,
    },
  ], []);

  // Sortiere Daten nach Jahr
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.jahr - b.jahr);
  }, [data]);

  // Spaltenüberschriften (Jahre)
  const columnHeaders = useMemo(() =>
    sortedData.map(d => d.jahr),
    [sortedData]
  );

  // Konvertiere Zeilen-Definitionen zu HorizontalTableRow-Format
  const rows: HorizontalTableRow[] = useMemo(() =>
    rowDefinitions.map(rowDef => {
      const values = sortedData.map(yearData => (yearData as unknown as Record<string, number>)[rowDef.key]);
      // Summe aller numerischen Werte berechnen
      const sum = values.reduce((acc, val) => acc + (typeof val === 'number' ? val : 0), 0);
      return {
        key: rowDef.key,
        label: rowDef.label,
        values,
        lastColumnValue: sum,
        highlightRow: rowDef.highlight,
        tooltip: rowDef.tooltip,
        valueColor: rowDef.valueColor,
      };
    }),
    [rowDefinitions, sortedData]
  );

  return (
    <Section title="GuV-Prognose" noPadding>
      <HorizontalTable
        headerLabel="Betriebsjahr"
        columnHeaders={columnHeaders}
        rows={rows}
        lastColumn={{ header: 'Summe' }}
        labelColumnWidth={250}
        dataColumnWidth={120}
      />
    </Section>
  );
};

export default React.memo(ProfitAndLossSection);
